import * as _ from 'lodash';
import { Component } from 'react';
import { connect } from 'react-redux';
import ReactTable from 'react-table';
import { window } from '../../../globals';
import ProjectsModule from '../../../redux/projects';
import type { Project } from '../../../types';
import { getHumanReadableBytes } from '../../../utils';
import { Icon, icons } from '../../generic';
import Button from '../../generic/Button';
import Flex from '../../generic/Flex';
import { TextInput } from '../../generic/Forms';
import { ICON_SIZE } from '../../generic/Icon/icon-constants';
import ProgressBar from '../../generic/ProgressBar';
import type { FileFieldUpdateDelegate, FileToUpload, FileToUploadAndStatus } from './UploadComponentPage';

type ReduxProps = {
  existingArchives: Project[];
  fetchAllProjects: (filters: object) => void;
};

type OwnProps = {
  onRemoveFile(fileName: string): void;
  files: FileToUploadAndStatus[];
  onFileFieldUpdated: FileFieldUpdateDelegate;
  fetcher: 'sbom' | 'archive';
};

type UploadComponentTableProps = ReduxProps & OwnProps;

class UploadComponentTable extends Component<UploadComponentTableProps> {
  componentDidMount() {
    if (window.user) {
      this.props.fetchAllProjects({
        organizationId: window.user.organizationId, fetcher: this.props.fetcher,
      });
    }
  }

  doesFileLocatorExist(name: string) {
    const locator = `${this.props.fetcher}+${window.user?.organizationId}/${name}`;
    return this.props.existingArchives.some(archive => archive.locator === locator);
  }

  isRowDisabled(row: FileToUploadAndStatus) {
    return row.status.bytesUploaded > 0 && !row.status.errorMessage;
  }

  nameHelperText(file: FileToUpload) {
    if (!file.customName || _.isEmpty(file.customName)) {
      return 'Required';
    }

    if (this.doesFileLocatorExist(file.customName)) {
      return 'Adding to existing project';
    }

    return 'Creating a new project';
  }

  getCols = () => [
    {
      id: 'fileName',
      accessor: 'name',
      Header: 'File Name',
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original: row }: { original: FileToUploadAndStatus }) =>
        <div className="margin-left-md">
          <Flex justify="flex-start">
            <div className="margin-right-lg">
              <Icon icon={icons.fileO} size={ICON_SIZE.LG} />
            </div>
            <div style={{ width: '100%', paddingRight: '30px' }}>
              <h5>{row.file.name}</h5>
              {
                row.status.bytesUploaded <= 0 && !row.status.errorMessage
                  ? <p>{row.file.size && getHumanReadableBytes(row.file.size)}</p>
                  : <ProgressBar
                      bytesFinished={row.status.bytesUploaded}
                      bytesTotal={row.file.size}
                      hasFailed={!!row.status.errorMessage}
                      errorMessage={row.status.errorMessage}
                    />
              }
            </div>
          </Flex>
        </div>,
    },
    {
      id: 'projectName',
      accessor: 'customName',
      Header: 'Project Name',
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original: row }: { original: FileToUploadAndStatus }) =>
        <>
          <TextInput
            label={`${row.file.name}-customName`}
            value={row.file.customName || ''}
            isRequired
            onChange={(val: string) => {
              this.props.onFileFieldUpdated('customName', val, row.file);
            }}
            isDisabled={this.isRowDisabled(row)}
          />
          <p>{this.nameHelperText(row.file)}</p>
        </>,
    },
    {
      id: 'revision',
      Header: 'Revision',
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original: row }: { original: FileToUploadAndStatus }) =>
        <TextInput
          label={`${row.file.name}-revision`}
          value={row.file.revision || ''}
          placeholder="v1.2.3"
          onChange={(val: string) => {
            this.props.onFileFieldUpdated('revision', val, row.file);
          }}
          isDisabled={this.isRowDisabled(row)}
        />,
    },
    {
      id: 'remove',
      width: 100,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original: row }: { original: FileToUploadAndStatus }) => {
        if (row.status.bytesUploaded <= 0) {
          return (
            <div className="margin-top-xs">
              <Button
                icon={icons.trash}
                size="small"
                type="error"
                isDisabled={this.isRowDisabled(row)}
                onClick={() => this.props.onRemoveFile(row.file.name)}>
                Remove
              </Button>
            </div>
          );
        }

        return null;
      },
    },
  ];

  render() {
    return (
      <ReactTable
        resizable={false}
        minRows={0}
        columns={this.getCols()}
        data={this.props.files}
        showPagination={false}
      />
    );
  }
}

const mapStateToProps = (state: Record<string, unknown>) => ({
  existingArchives: ProjectsModule.selectors.getProjectList(state),
});

const mapDispatchToProps = {
  fetchAllProjects: ProjectsModule.actions.fetchAll,
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadComponentTable);
